import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Minus, Plus, Trash2, ArrowLeft, Loader2, ShoppingBag, CheckCircle2 } from "lucide-react";
import { PaymentForm, CreditCard } from "react-square-web-payments-sdk";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = {
  CART: "cart",
  DETAILS: "details",
  PAYMENT: "payment",
  PROCESSING: "processing",
  SUCCESS: "success",
};

export default function CartSheet() {
  const {
    items,
    updateQuantity,
    removeItem,
    isOpen,
    setIsOpen,
    subtotal,
    tax,
    total,
    count,
    clearCart,
  } = useCart();

  const [step, setStep] = useState(STEPS.CART);
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    delivery_method: "pickup",
  });
  const [errors, setErrors] = useState({});
  const [confirmation, setConfirmation] = useState(null);
  const [serverError, setServerError] = useState(null);

  // Fetch Square config once
  useEffect(() => {
    axios
      .get(`${API}/config`)
      .then((r) => setConfig(r.data))
      .catch(() => setConfig(null));
  }, []);

  // Reset wizard when sheet closes (called from Sheet's onOpenChange)
  const handleOpenChange = (open) => {
    setIsOpen(open);
    if (!open) {
      // Slight delay so closing animation finishes before resetting view
      setTimeout(() => {
        setStep(STEPS.CART);
        setServerError(null);
        setConfirmation(null);
      }, 300);
    }
  };

  const validateDetails = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!form.email.trim()) e.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Invalid email";
    if (!form.phone.trim()) e.phone = "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleProceedToDetails = () => {
    if (items.length === 0) return;
    setStep(STEPS.DETAILS);
  };

  const handleProceedToPayment = () => {
    if (!validateDetails()) return;
    setStep(STEPS.PAYMENT);
  };

  const handleTokenized = async (token) => {
    if (!token || !token.token) {
      toast.error("Payment tokenization failed. Please try again.");
      return;
    }
    setStep(STEPS.PROCESSING);
    setServerError(null);
    try {
      const payload = {
        source_id: token.token,
        customer_name: form.name,
        customer_email: form.email,
        customer_phone: form.phone,
        delivery_method: form.delivery_method,
        items: items.map((i) => ({
          product_id: i.product_id,
          name: i.name,
          price: i.price,
          quantity: i.quantity,
        })),
      };
      const res = await axios.post(`${API}/checkout/payment`, payload);
      setConfirmation(res.data);
      setStep(STEPS.SUCCESS);
      clearCart();
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Payment failed. Please try again.";
      setServerError(typeof detail === "string" ? detail : "Payment failed");
      setStep(STEPS.PAYMENT);
      toast.error(typeof detail === "string" ? detail : "Payment failed");
    }
  };

  const closeAndReset = () => {
    setIsOpen(false);
    setTimeout(() => {
      setStep(STEPS.CART);
      setConfirmation(null);
      setForm({ name: "", email: "", phone: "", delivery_method: "pickup" });
      setErrors({});
      setServerError(null);
    }, 300);
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        data-testid="cart-sheet"
        className="w-full sm:max-w-md bg-[#FDFBF7] border-l border-[#E5DFD3] p-0 flex flex-col"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#E5DFD3]">
          <SheetTitle className="font-display text-2xl text-[#2C4C3B] tracking-tight">
            {step === STEPS.CART && "Your cart"}
            {step === STEPS.DETAILS && "Your details"}
            {step === STEPS.PAYMENT && "Payment"}
            {step === STEPS.PROCESSING && "Processing"}
            {step === STEPS.SUCCESS && "Order confirmed"}
          </SheetTitle>
          {step !== STEPS.SUCCESS && step !== STEPS.PROCESSING && (
            <div className="text-xs uppercase tracking-[0.25em] text-[#4A5D53]">
              {count} {count === 1 ? "item" : "items"} &middot; ${total.toFixed(2)} total
            </div>
          )}
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          {step === STEPS.CART && (
            <CartItems
              items={items}
              updateQuantity={updateQuantity}
              removeItem={removeItem}
            />
          )}

          {step === STEPS.DETAILS && (
            <DetailsForm
              form={form}
              setForm={setForm}
              errors={errors}
              onBack={() => setStep(STEPS.CART)}
            />
          )}

          {step === STEPS.PAYMENT && (
            <PaymentStep
              config={config}
              onBack={() => setStep(STEPS.DETAILS)}
              onTokenized={handleTokenized}
              serverError={serverError}
            />
          )}

          {step === STEPS.PROCESSING && (
            <div
              data-testid="processing-state"
              className="h-full flex flex-col items-center justify-center text-center py-20"
            >
              <Loader2 className="h-8 w-8 animate-spin text-[#D96C2B] mb-6" />
              <div className="font-display text-xl text-[#2C4C3B]">
                Charging your card…
              </div>
              <div className="text-sm text-[#4A5D53] mt-2">
                Please don&apos;t close this window.
              </div>
            </div>
          )}

          {step === STEPS.SUCCESS && (
            <SuccessState
              confirmation={confirmation}
              onClose={closeAndReset}
            />
          )}
        </div>

        {/* Footer / totals + CTA */}
        {(step === STEPS.CART || step === STEPS.DETAILS) && items.length > 0 && (
          <div className="border-t border-[#E5DFD3] px-6 py-5 bg-[#F4F1EA]">
            <Totals subtotal={subtotal} tax={tax} total={total} />
            {step === STEPS.CART && (
              <button
                data-testid="proceed-to-checkout-btn"
                onClick={handleProceedToDetails}
                className="mt-5 w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-[#D96C2B] text-[#FDFBF7] uppercase tracking-wider text-sm hover:bg-[#B75718] transition-colors disabled:opacity-50"
                disabled={items.length === 0}
              >
                Checkout — ${total.toFixed(2)}
              </button>
            )}
            {step === STEPS.DETAILS && (
              <button
                data-testid="proceed-to-payment-btn"
                onClick={handleProceedToPayment}
                className="mt-5 w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-[#D96C2B] text-[#FDFBF7] uppercase tracking-wider text-sm hover:bg-[#B75718] transition-colors"
              >
                Continue to Payment
              </button>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function CartItems({ items, updateQuantity, removeItem }) {
  if (items.length === 0) {
    return (
      <div
        data-testid="empty-cart"
        className="h-full flex flex-col items-center justify-center text-center py-20"
      >
        <ShoppingBag className="h-10 w-10 text-[#4A5D53] mb-5" />
        <div className="font-display text-xl text-[#2C4C3B]">
          Your cart is empty
        </div>
        <div className="text-sm text-[#4A5D53] mt-2 max-w-xs">
          Add a bottle or two and they&apos;ll show up here.
        </div>
      </div>
    );
  }

  return (
    <ul className="space-y-6" data-testid="cart-items-list">
      {items.map((item) => (
        <li
          key={item.product_id}
          data-testid={`cart-item-${item.product_id}`}
          className="flex gap-4 pb-6 border-b border-[#E5DFD3] last:border-b-0 last:pb-0"
        >
          <div className="h-24 w-20 bg-[#F4F1EA] flex items-center justify-center flex-shrink-0">
            <img
              src={item.image_url}
              alt={item.name}
              className="h-24 w-auto object-contain"
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex justify-between gap-3">
              <div>
                <div className="font-display text-lg text-[#2C4C3B] leading-tight">
                  {item.name}
                </div>
                <div className="text-xs uppercase tracking-widest text-[#4A5D53] mt-1">
                  {item.size}
                </div>
              </div>
              <div className="text-[#1F2924] font-medium whitespace-nowrap">
                ${(item.price * item.quantity).toFixed(2)}
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between">
              <div className="inline-flex items-center border border-[#E5DFD3] bg-white">
                <button
                  data-testid={`decrease-qty-${item.product_id}`}
                  onClick={() =>
                    updateQuantity(item.product_id, item.quantity - 1)
                  }
                  className="px-2 py-2 text-[#2C4C3B] hover:bg-[#F4F1EA] transition-colors"
                  aria-label="Decrease quantity"
                >
                  <Minus className="h-3.5 w-3.5" />
                </button>
                <span
                  data-testid={`qty-${item.product_id}`}
                  className="w-8 text-center text-sm text-[#1F2924]"
                >
                  {item.quantity}
                </span>
                <button
                  data-testid={`increase-qty-${item.product_id}`}
                  onClick={() =>
                    updateQuantity(item.product_id, item.quantity + 1)
                  }
                  className="px-2 py-2 text-[#2C4C3B] hover:bg-[#F4F1EA] transition-colors"
                  aria-label="Increase quantity"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
              <button
                data-testid={`remove-item-${item.product_id}`}
                onClick={() => removeItem(item.product_id)}
                className="text-xs uppercase tracking-widest text-[#4A5D53] hover:text-[#D96C2B] transition-colors inline-flex items-center gap-1.5"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Remove
              </button>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function DetailsForm({ form, setForm, errors, onBack }) {
  return (
    <div data-testid="details-form">
      <button
        onClick={onBack}
        data-testid="back-to-cart-btn"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#4A5D53] hover:text-[#D96C2B] transition-colors mb-6"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to cart
      </button>

      <div className="space-y-5">
        <div>
          <Label
            htmlFor="name"
            className="text-xs uppercase tracking-widest text-[#4A5D53]"
          >
            Full Name
          </Label>
          <Input
            id="name"
            data-testid="input-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="mt-2 bg-white border-[#E5DFD3] focus-visible:ring-[#D96C2B] rounded-none"
            placeholder="Jane Smith"
          />
          {errors.name && (
            <div className="text-xs text-red-700 mt-1">{errors.name}</div>
          )}
        </div>
        <div>
          <Label
            htmlFor="email"
            className="text-xs uppercase tracking-widest text-[#4A5D53]"
          >
            Email
          </Label>
          <Input
            id="email"
            type="email"
            data-testid="input-email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="mt-2 bg-white border-[#E5DFD3] focus-visible:ring-[#D96C2B] rounded-none"
            placeholder="jane@example.com"
          />
          {errors.email && (
            <div className="text-xs text-red-700 mt-1">{errors.email}</div>
          )}
        </div>
        <div>
          <Label
            htmlFor="phone"
            className="text-xs uppercase tracking-widest text-[#4A5D53]"
          >
            Phone
          </Label>
          <Input
            id="phone"
            type="tel"
            data-testid="input-phone"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="mt-2 bg-white border-[#E5DFD3] focus-visible:ring-[#D96C2B] rounded-none"
            placeholder="(352) 555-0100"
          />
          {errors.phone && (
            <div className="text-xs text-red-700 mt-1">{errors.phone}</div>
          )}
        </div>

        <div>
          <div className="text-xs uppercase tracking-widest text-[#4A5D53] mb-3">
            Fulfillment
          </div>
          <div
            className="grid grid-cols-2 gap-3"
            data-testid="delivery-method-group"
          >
            {[
              { key: "pickup", label: "Local Pickup", sub: "Free" },
              { key: "delivery", label: "Local Delivery", sub: "Free" },
            ].map((opt) => {
              const active = form.delivery_method === opt.key;
              return (
                <button
                  key={opt.key}
                  data-testid={`delivery-${opt.key}`}
                  type="button"
                  onClick={() =>
                    setForm({ ...form, delivery_method: opt.key })
                  }
                  className={`text-left px-4 py-3 border transition-colors ${
                    active
                      ? "border-[#2C4C3B] bg-[#2C4C3B] text-[#FDFBF7]"
                      : "border-[#E5DFD3] bg-white text-[#1F2924] hover:border-[#2C4C3B]"
                  }`}
                >
                  <div className="text-sm font-medium">{opt.label}</div>
                  <div
                    className={`text-[10px] uppercase tracking-widest mt-1 ${
                      active ? "text-[#FDFBF7]/70" : "text-[#4A5D53]"
                    }`}
                  >
                    {opt.sub}
                  </div>
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-[#4A5D53] mt-3 leading-relaxed">
            We&apos;ll follow up by phone or email to arrange the {form.delivery_method === "pickup" ? "pickup" : "delivery"} time and location.
          </p>
        </div>
      </div>
    </div>
  );
}

// Pre-defined outside the component so it has a stable reference (avoids
// triggering unnecessary <CreditCard> re-renders on every parent render).
const SQ_BUTTON_PROPS = {
  css: {
    backgroundColor: "#D96C2B",
    fontSize: "13px",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: "#FDFBF7",
    borderRadius: "0px",
    "&:hover": { backgroundColor: "#B75718" },
  },
};

function PaymentStep({ config, onBack, onTokenized, serverError }) {
  if (!config) {
    return (
      <div className="py-20 flex justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#D96C2B]" />
      </div>
    );
  }

  return (
    <div data-testid="payment-step">
      <button
        onClick={onBack}
        data-testid="back-to-details-btn"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#4A5D53] hover:text-[#D96C2B] transition-colors mb-6"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to details
      </button>

      <div className="mb-5 text-sm text-[#4A5D53] leading-relaxed">
        Enter your card below. Payments are processed securely by Square.
      </div>

      {serverError && (
        <div
          data-testid="payment-error"
          className="mb-5 px-4 py-3 bg-red-50 border border-red-200 text-red-800 text-sm"
        >
          {serverError}
        </div>
      )}

      <div className="sq-card-wrapper bg-white p-4 border border-[#E5DFD3]">
        <PaymentForm
          applicationId={config.square_application_id}
          locationId={config.square_location_id}
          cardTokenizeResponseReceived={onTokenized}
        >
          <CreditCard buttonProps={SQ_BUTTON_PROPS}>Pay now</CreditCard>
        </PaymentForm>
      </div>

      <div className="mt-4 text-[11px] text-[#4A5D53] leading-relaxed">
        Payments are processed securely by Square. We never see or store your card details.
      </div>
    </div>
  );
}

function SuccessState({ confirmation, onClose }) {
  if (!confirmation) return null;
  return (
    <div data-testid="order-confirmation" className="py-6">
      <div className="flex flex-col items-center text-center">
        <div className="h-16 w-16 rounded-full bg-[#2C4C3B] flex items-center justify-center mb-5">
          <CheckCircle2 className="h-8 w-8 text-[#FDFBF7]" />
        </div>
        <div className="font-display text-3xl text-[#2C4C3B] leading-tight">
          Thank you, {confirmation.customer_name?.split(" ")[0] || "friend"}!
        </div>
        <div className="text-sm text-[#4A5D53] mt-3 max-w-xs">
          Your order has been received and your payment was successful. We&apos;ll
          reach out shortly to arrange{" "}
          {confirmation.delivery_method === "pickup" ? "pickup" : "delivery"}.
        </div>
      </div>

      <div className="mt-8 bg-[#F4F1EA] p-5 text-sm">
        <div className="flex justify-between items-start gap-3 text-[10px] uppercase tracking-widest text-[#4A5D53] mb-3">
          <span>Order ID</span>
          <span
            data-testid="confirmation-order-id"
            className="font-mono normal-case tracking-normal text-[11px] text-[#1F2924] break-all text-right"
          >
            {confirmation.order_id}
          </span>
        </div>
        <ul className="space-y-2 text-[#1F2924]">
          {confirmation.items?.map((it) => (
            <li
              key={it.product_id}
              className="flex justify-between"
              data-testid={`conf-item-${it.product_id}`}
            >
              <span>
                {it.name} × {it.quantity}
              </span>
              <span>${(it.price * it.quantity).toFixed(2)}</span>
            </li>
          ))}
        </ul>
        <div className="mt-4 pt-4 border-t border-[#E5DFD3] space-y-1.5 text-sm">
          <div className="flex justify-between text-[#4A5D53]">
            <span>Subtotal</span>
            <span>${confirmation.subtotal?.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-[#4A5D53]">
            <span>Tax (7%)</span>
            <span>${confirmation.tax_amount?.toFixed(2)}</span>
          </div>
          <div
            className="flex justify-between font-display text-lg text-[#2C4C3B] pt-1"
            data-testid="confirmation-total"
          >
            <span>Total</span>
            <span>${confirmation.total_amount?.toFixed(2)}</span>
          </div>
        </div>
      </div>

      <div className="mt-6 text-xs text-[#4A5D53] leading-relaxed">
        A receipt note has been logged to{" "}
        <span className="text-[#2C4C3B]">{confirmation.customer_email}</span>.
        Questions? Call us at <span className="text-[#2C4C3B]">352-552-4074</span>.
      </div>

      <a
        href="/orders/lookup"
        data-testid="confirmation-track-link"
        className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#2C4C3B] hover:text-[#D96C2B] transition-colors"
      >
        Track this order &rarr;
      </a>

      <button
        data-testid="close-confirmation-btn"
        onClick={onClose}
        className="mt-4 w-full px-6 py-4 bg-[#2C4C3B] text-[#FDFBF7] uppercase tracking-wider text-sm hover:bg-[#1C3327] transition-colors"
      >
        Continue browsing
      </button>
    </div>
  );
}

function Totals({ subtotal, tax, total }) {
  return (
    <div className="space-y-1.5 text-sm" data-testid="cart-totals">
      <div className="flex justify-between text-[#4A5D53]">
        <span>Subtotal</span>
        <span data-testid="totals-subtotal">${subtotal.toFixed(2)}</span>
      </div>
      <div className="flex justify-between text-[#4A5D53]">
        <span>Sales tax (7%)</span>
        <span data-testid="totals-tax">${tax.toFixed(2)}</span>
      </div>
      <div className="flex justify-between font-display text-xl text-[#2C4C3B] pt-2 border-t border-[#E5DFD3] mt-2">
        <span>Total</span>
        <span data-testid="totals-total">${total.toFixed(2)}</span>
      </div>
    </div>
  );
}
